import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import PendingApprovalsPanel, { SubmissionBadge } from './PendingApprovalsPanel';
import TimePicker from './TimePicker';
import { IcoCalendar, IcoTrash, IcoRefreshCw, IcoAlertTriangle } from '../dashboard/DashIcons';
import {
  getMonday, calcHours,
  emptyShift, emptyShift2, scheduleToGrid, totalDayHours,
  getPeriodWeekStarts, getPeriodDates, mergeSchedulesToGrid, gridToShiftsByWeek,
  toISODate, applyWeekdayPatternAcrossPeriod, formatPeriodLabel,
} from '../../utils/scheduleHelpers';
import { getPayPeriodForDate, formatPayDate } from '../../hooks/payPeriodUtils';
import '../../styles/admin-dashboard.css';

export default function SchedulePage() {
  const { user } = useAuth();
  const location = useLocation();
  const token = user?.token;
  const isSuperAdmin    = user?.role === 'superadmin';
  const isManager       = user?.role === 'manager';
  const hasFullAccess   = isSuperAdmin || user?.role === 'adminassistant';
  const canManageSchedules = isSuperAdmin || user?.role === 'adminassistant' || isManager;

  const headers = useMemo(() => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  }), [token]);

  const [allEmployees, setAllEmployees]       = useState([]);
  const [employeesReady, setEmployeesReady]   = useState(false);
  const [scheduledIds, setScheduledIds]       = useState([]);
  const [currentMonday, setCurrentMonday]     = useState(() => getMonday(new Date()));
  const [rawSchedulesByWeek, setRawSchedulesByWeek] = useState({});
  const [scheduleViewMode, setScheduleViewMode] = useState('payPeriod');
  const [templates, setTemplates]             = useState([]);
  const [saveStatus, setSaveStatus]           = useState(null);
  const [templateName, setTemplateName]       = useState('');
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [loading, setLoading]                 = useState(true);
  const [submitPayrollStatus, setSubmitPayrollStatus] = useState(null);
  const [drillSubmitStatus, setDrillSubmitStatus] = useState(null);
  const [conflictDialog, setConflictDialog]   = useState(false);
  const [, setScheduleSubmitted]              = useState(false);
  const [showTemplateDrawer, setShowTemplateDrawer] = useState(false);
  const [templateSearch, setTemplateSearch]   = useState('');
  const [showApprovalsPanel, setShowApprovalsPanel] = useState(false);
  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [rejectionNotes, setRejectionNotes]   = useState(null);
  const [rejectionReviewedBy, setRejectionReviewedBy] = useState(null);
  const [rejectionReviewedAt, setRejectionReviewedAt] = useState(null);
  const [orgLunchSettings, setOrgLunchSettings] = useState({ mode: 'minutes', flatRatePerEntry: 0 });
  const [payPeriodConfig, setPayPeriodConfig] = useState(null);
  const [drillEmployeeId, setDrillEmployeeId] = useState(null);
  const [showAddModal, setShowAddModal]       = useState(false);
  const [addModalSearch, setAddModalSearch]   = useState('');
  const [showDefaultModal, setShowDefaultModal] = useState(false);
  const [defaultEmployees, setDefaultEmployees] = useState([]);
  const [defaultLoading, setDefaultLoading]   = useState(false);
  const [pendingCount, setPendingCount]       = useState(0);
  const [defaultSchedulesRaw, setDefaultSchedulesRaw] = useState([]);
  const [confirmDialog, setConfirmDialog]     = useState(null);
  const [drillTemplateName, setDrillTemplateName] = useState('');
  const [showDrillSaveTemplate, setShowDrillSaveTemplate] = useState(false);
  const [drillSaveStatus, setDrillSaveStatus] = useState(null);
  const [showUpdateTemplatePicker, setShowUpdateTemplatePicker] = useState(false);
  const [copyingLastWeek, setCopyingLastWeek]       = useState(false);
  const [copyLastWeekMsg, setCopyLastWeekMsg]       = useState(null);
  const [loadedTemplateId, setLoadedTemplateId]     = useState(null);
  const [loadedTemplateName, setLoadedTemplateName] = useState(null);
  // Ref mirrors state so the value is never stale inside callbacks
  const loadedTemplateIdRef   = useRef(null);
  const loadedTemplateNameRef = useRef(null);

  // isPayPeriodMode falls back to week-by-week automatically if the org has no
  // payPeriodConfig configured yet, regardless of the saved setting.
  const isPayPeriodMode = scheduleViewMode !== 'week' && !!payPeriodConfig?.anchorDate;

  // periodRange is always populated — either the org's actual configured pay
  // period (payPeriod mode), or a synthesized Monday–Sunday week (week mode /
  // fallback). Everything downstream (grid, fetch, save, render) is driven by
  // this single range so there's one code path instead of two.
  const periodRange = useMemo(() => {
    if (isPayPeriodMode) {
      const p = getPayPeriodForDate(payPeriodConfig, currentMonday);
      if (p) return p;
    }
    const weekStart = getMonday(currentMonday);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    return { start: toISODate(weekStart), end: toISODate(weekEnd), payDate: null };
  }, [isPayPeriodMode, payPeriodConfig, currentMonday]);

  const periodDates = useMemo(
    () => getPeriodDates(periodRange.start, periodRange.end),
    [periodRange.start, periodRange.end]
  );

  const periodWeekStarts = useMemo(
    () => getPeriodWeekStarts(periodRange.start, periodRange.end),
    [periodRange.start, periodRange.end]
  );

  const grid = useMemo(
    () => mergeSchedulesToGrid(rawSchedulesByWeek, allEmployees, periodDates),
    [rawSchedulesByWeek, allEmployees, periodDates]
  );

  const [gridPatch, setGridPatch] = useState({});

  const mergedGrid = useMemo(() => {
    if (Object.keys(gridPatch).length === 0) return grid;
    const merged = { ...grid };
    for (const empId of Object.keys(gridPatch)) {
      merged[empId] = { ...(grid[empId] || {}), ...gridPatch[empId] };
      for (const day of Object.keys(gridPatch[empId] || {})) {
        merged[empId][day] = { ...(grid[empId]?.[day] || {}), ...gridPatch[empId][day] };
      }
    }
    return merged;
  }, [grid, gridPatch]);

  const resetPatch = useCallback(() => {
    setGridPatch({});
    // Clear loaded template tracking when the schedule resets
    setLoadedTemplateId(null);
    setLoadedTemplateName(null);
    loadedTemplateIdRef.current   = null;
    loadedTemplateNameRef.current = null;
    setShowUpdateTemplatePicker(false);
  }, []);

  // Splits a date-keyed grid back into one shifts[] payload per week and saves
  // each with the existing single-week POST /api/schedules endpoint — the one
  // place all save call sites funnel through, so periods spanning N weeks
  // transparently become N parallel saves instead of duplicating this loop.
  const saveGridToServer = useCallback(async (gridToSave, employeesToSave) => {
    const shiftsByWeek = gridToShiftsByWeek(gridToSave, employeesToSave, periodDates);
    await Promise.all(
      Object.entries(shiftsByWeek).map(([weekStartISO, shifts]) =>
        fetch('/api/schedules', { method: 'POST', headers, body: JSON.stringify({ weekStart: weekStartISO, shifts }) })
      )
    );
  }, [periodDates, headers]);

  const scheduledEmployees = useMemo(
    () => allEmployees.filter(e => scheduledIds.includes(e._id)),
    [allEmployees, scheduledIds]
  );

  // IDs of employees that are directly assigned to the current user as their manager.
  // Only relevant for superadmin/adminassistant who are also acting as a manager.
  const myTeamIds = useMemo(() => {
    const userId = (user?._id || user?.id)?.toString();
    if (!userId || !hasFullAccess) return new Set();
    return new Set(
      allEmployees
        .filter(emp => emp.managerIds?.some(id => id.toString() === userId))
        .map(emp => emp._id)
    );
  }, [allEmployees, user, hasFullAccess]);

  const [scheduleFilter, setScheduleFilter] = useState('all');

  // Reset filter when week changes so stale "My Team" view doesn't confuse
  useEffect(() => { setScheduleFilter('all'); }, [currentMonday]);

  const displayedScheduledEmployees = useMemo(() => {
    if (scheduleFilter === 'myTeam' && myTeamIds.size > 0) {
      return scheduledEmployees.filter(e => myTeamIds.has(e._id));
    }
    return scheduledEmployees;
  }, [scheduledEmployees, scheduleFilter, myTeamIds]);

  const drillEmployee = useMemo(
    () => allEmployees.find(e => e._id === drillEmployeeId) || null,
    [allEmployees, drillEmployeeId]
  );

  const showConfirm = useCallback((message, onConfirm) => {
    setConfirmDialog({ message, onConfirm });
  }, []);

  const fetchEmployees = useCallback(async () => {
    try {
      const endpoint = hasFullAccess ? '/api/staff' : '/api/staff/department';
      const res = await fetch(endpoint, { headers });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : data.employees || [];
        setAllEmployees(list.filter(e => e.role !== 'superadmin'));
      }
    } catch (err) { console.error('Error fetching employees:', err); }
  }, [headers, hasFullAccess]);

  // Re-fetch employees on mount and whenever the user navigates to this page
  useEffect(() => {
    fetchEmployees();
  }, [fetchEmployees, location.key]);

  // Mark employees as ready the first time they load, so the schedule fetch can run
  useEffect(() => {
    if (allEmployees.length > 0 && !employeesReady) {
      setEmployeesReady(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allEmployees.length]);

  // Listen for department assignment changes from other tabs/pages
  useEffect(() => {
    let bc;
    try {
      bc = new BroadcastChannel('dept-updates');
      bc.onmessage = () => fetchEmployees();
    } catch {}
    return () => { try { bc?.close(); } catch {} };
  }, [fetchEmployees]);

  useEffect(() => {
    if (!user?.organizationId) return;
    const fetchOrgSettings = async () => {
      try {
        const res = await fetch(`/api/organizations/${user.organizationId}`, { headers });
        if (res.ok) {
          const data = await res.json();
          if (data.lunchSettings) setOrgLunchSettings(data.lunchSettings);
          if (data.payPeriodConfig) setPayPeriodConfig(data.payPeriodConfig);
          setScheduleViewMode(data.systemPreferences?.scheduleViewMode === 'week' ? 'week' : 'payPeriod');
        }
      } catch (err) { console.error('Error fetching org settings:', err); }
    };
    fetchOrgSettings();
  }, [headers, user?.organizationId]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    const fetchPending = async () => {
      try {
        const res = await fetch('/api/payroll/pending', { headers });
        if (res.ok) {
          const data = await res.json();
          setPendingCount((data.submissions || []).length);
        }
      } catch { /* silent */ }
    };
    fetchPending();
  }, [headers, isSuperAdmin]);

  const defaultFetchedRef = useRef(false);

  const fetchDefaultEmployees = useCallback(async () => {
    setDefaultLoading(true);
    try {
      const res = await fetch('/api/schedules/default', { headers });
      if (res.ok) {
        const data = await res.json();
        setDefaultSchedulesRaw(data?.shifts || []);
        if (data?.shifts?.length > 0) {
          const empIds = [...new Set(data.shifts.map(s => s.employeeId?.toString()))];
          const result = empIds.map(id => {
            const emp = allEmployees.find(e => e._id === id);
            if (emp) return { id, name: `${emp.firstName} ${emp.lastName}`, role: emp.role };
            const shift = data.shifts.find(s => s.employeeId?.toString() === id);
            return { id, name: shift?.employeeName || 'Unknown', role: '' };
          });
          setDefaultEmployees(result);
        } else {
          setDefaultEmployees([]);
        }
      }
    } catch { /* silent */ }
    finally { setDefaultLoading(false); }
  }, [headers, allEmployees]);

  useEffect(() => {
    if (allEmployees.length > 0 && !defaultFetchedRef.current) {
      defaultFetchedRef.current = true;
      fetchDefaultEmployees();
    }
  }, [allEmployees, fetchDefaultEmployees]);

  // Rolls up submission status across every week in the period — worst case wins
  // (rejected > pending > approved) so the badge reflects the whole period at a
  // glance. In week mode periodWeekStarts has exactly one entry, same as before.
  const fetchSubmissionStatus = useCallback(async () => {
    try {
      const results = await Promise.all(periodWeekStarts.map(async (monday) => {
        const weekStart = toISODate(monday);
        const res = await fetch(`/api/payroll/submissions?weekStart=${weekStart}`, { headers });
        if (!res.ok) return null;
        const data = await res.json();
        const subs = data.submissions || [];
        return subs.find(s => s.status === 'approved')
          || subs.find(s => s.status === 'pending')
          || subs.find(s => s.status === 'rejected')
          || null;
      }));
      const matches = results.filter(Boolean);
      const worst = matches.find(m => m.status === 'rejected')
        || matches.find(m => m.status === 'pending')
        || matches.find(m => m.status === 'approved')
        || null;
      setSubmissionStatus(worst?.status || null);
      setScheduleSubmitted(matches.length > 0 && matches.every(m => m.status !== 'rejected'));
      if (worst?.reviewNotes) {
        setRejectionNotes(worst.reviewNotes);
        const rb = worst.reviewedBy;
        setRejectionReviewedBy(rb ? (rb.firstName ? `${rb.firstName} ${rb.lastName || ''}`.trim() : rb.name || rb.email) : null);
        setRejectionReviewedAt(worst.reviewedAt || null);
      } else {
        setRejectionNotes(null);
        setRejectionReviewedBy(null);
        setRejectionReviewedAt(null);
      }
    } catch { /* silent */ }
  }, [periodWeekStarts, headers]);

  // Fetches every week's Schedule doc that the current period spans (1 for a
  // plain week, more for biweekly/semimonthly/monthly) and stores them keyed by
  // weekStartISO so the grid memo can merge them into one continuous view.
  const fetchSchedule = useCallback(async () => {
    setLoading(true);
    resetPatch();
    try {
      const results = await Promise.all(periodWeekStarts.map(async (monday) => {
        const weekStart = toISODate(monday);
        const res = await fetch(`/api/schedules?weekStart=${weekStart}`, { headers });
        const data = res.ok ? await res.json() : null;
        return [weekStart, data];
      }));
      const byWeek = Object.fromEntries(results);
      setRawSchedulesByWeek(byWeek);
      const allShifts = Object.values(byWeek).flatMap(d => d?.shifts || []);
      setScheduledIds(allShifts.length ? [...new Set(allShifts.map(s => s.employeeId?.toString()))] : []);
      setScheduleSubmitted(Object.values(byWeek).some(d => d?.submittedToPayroll));
    } catch {
      setRawSchedulesByWeek({});
      setScheduledIds([]);
    }
    finally { setLoading(false); }
  }, [periodWeekStarts, headers, resetPatch]);

  // Fetch schedule when ready (initial load) or when week/page changes.
  // Intentionally excludes allEmployees — employee refreshes (BroadcastChannel,
  // location.key) should not wipe unsaved schedule changes.
  useEffect(() => {
    if (!employeesReady) return;
    fetchSchedule();
    fetchSubmissionStatus();
  }, [fetchSchedule, fetchSubmissionStatus, employeesReady, location.key]);

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await fetch('/api/schedules/templates', { headers });
        if (res.ok) setTemplates(await res.json());
      } catch (err) { console.error(err); }
    };
    fetchTemplates();
  }, [headers]);

  // ── Cell update helpers ────────────────────────────────────────────────────
  const updateCell = useCallback((empId, day, field, value) => {
    setGridPatch(prev => {
      const dayData = { ...(prev[empId]?.[day] || mergedGrid[empId]?.[day] || {}), [field]: value };
      if (field === 'startTime' || field === 'endTime' || field === 'lunchBreak') {
        const lb = dayData.lunchBreak;
        dayData.hours = calcHours(dayData.startTime, dayData.endTime, lb?.enabled ? (lb.durationMinutes || 30) : 0);
      }
      return { ...prev, [empId]: { ...(prev[empId] || {}), [day]: dayData } };
    });
  }, [mergedGrid]);

  const updateShift2Cell = useCallback((empId, day, field, value) => {
    setGridPatch(prev => {
      const base  = prev[empId]?.[day] || mergedGrid[empId]?.[day] || {};
      const shift2 = { ...(base.shift2 || {}), [field]: value };
      if (field === 'startTime' || field === 'endTime' || field === 'lunchBreak') {
        shift2.hours = calcHours(shift2.startTime, shift2.endTime, shift2.lunchBreak?.enabled ? (shift2.lunchBreak.durationMinutes || 30) : 0);
      }
      return { ...prev, [empId]: { ...(prev[empId] || {}), [day]: { ...base, shift2 } } };
    });
  }, [mergedGrid]);

  const addShift2    = useCallback((empId, day) => updateCell(empId, day, 'shift2', emptyShift2()), [updateCell]);
  const removeShift2 = useCallback((empId, day) => updateCell(empId, day, 'shift2', null), [updateCell]);
  const toggleOff = useCallback(async (empId, day) => {
    const cur = mergedGrid[empId]?.[day] || emptyShift();
    const newOff = !cur.off;
    const updatedShift = { ...(mergedGrid[empId]?.[day] || emptyShift()), off: newOff };

    // Update in-memory immediately
    setGridPatch(prev => ({
      ...prev,
      [empId]: {
        ...(prev[empId] || {}),
        [day]: { ...(prev[empId]?.[day] || mergedGrid[empId]?.[day] || {}), off: newOff },
      },
    }));

    // Save to server with the updated state so the change persists
    const newGrid = { ...mergedGrid, [empId]: { ...(mergedGrid[empId] || {}), [day]: updatedShift } };
    const empList = allEmployees.filter(e => e._id === empId);
    try {
      await saveGridToServer(newGrid, empList);
    } catch (err) {
      console.error('Error saving mark off:', err);
    }
  }, [mergedGrid, allEmployees, saveGridToServer]);

  // Wipe a single day back to a blank empty shift (different from "Mark Off"
  // which keeps the day visible as "Off" — this makes it completely blank)
  const clearDay = useCallback(async (empId, day) => {
    const blank = emptyShift();

    setGridPatch(prev => ({
      ...prev,
      [empId]: { ...(prev[empId] || {}), [day]: blank },
    }));

    const newGrid = { ...mergedGrid, [empId]: { ...(mergedGrid[empId] || {}), [day]: blank } };
    const empList = allEmployees.filter(e => e._id === empId);
    try {
      await saveGridToServer(newGrid, empList);
    } catch (err) {
      console.error('Error saving clear day:', err);
    }
  }, [mergedGrid, allEmployees, saveGridToServer]);

  const togglePTO = useCallback((empId, day) => {
    const cur = mergedGrid[empId]?.[day] || emptyShift();
    updateCell(empId, day, 'isPTO', !cur.isPTO);
  }, [mergedGrid, updateCell]);

  const toggleLunch = useCallback((empId, day) => {
    const cur      = mergedGrid[empId]?.[day] || emptyShift();
    const newLunch = { ...(cur.lunchBreak || {}), enabled: !cur.lunchBreak?.enabled };
    updateCell(empId, day, 'lunchBreak', newLunch);
  }, [mergedGrid, updateCell]);

  const toggleLunch2 = useCallback((empId, day) => {
    const base   = mergedGrid[empId]?.[day] || emptyShift();
    const shift2 = base.shift2 || emptyShift2();
    const newLunch = { ...(shift2.lunchBreak || {}), enabled: !shift2.lunchBreak?.enabled };
    updateShift2Cell(empId, day, 'lunchBreak', newLunch);
  }, [mergedGrid, updateShift2Cell]);

  const setLunchDuration = useCallback((empId, day, minutes) => {
    const cur      = mergedGrid[empId]?.[day] || emptyShift();
    const newLunch = { ...(cur.lunchBreak || {}), durationMinutes: minutes };
    updateCell(empId, day, 'lunchBreak', newLunch);
  }, [mergedGrid, updateCell]);

  const setLunchDuration2 = useCallback((empId, day, minutes) => {
    const base   = mergedGrid[empId]?.[day] || emptyShift();
    const shift2 = base.shift2 || emptyShift2();
    const newLunch = { ...(shift2.lunchBreak || {}), durationMinutes: minutes };
    updateShift2Cell(empId, day, 'lunchBreak', newLunch);
  }, [mergedGrid, updateShift2Cell]);

  // ── Schedule manipulation ──────────────────────────────────────────────────
  const addToSchedule = useCallback(async (emp) => {
    if (scheduledIds.includes(emp._id)) { setDrillEmployeeId(emp._id); return; }
    const newIds = [...scheduledIds, emp._id];
    const empShifts = Object.fromEntries(periodDates.map(pd => [pd.dateISO, emptyShift()]));
    setScheduledIds(newIds);
    setGridPatch(prev => ({ ...prev, [emp._id]: empShifts }));
    setDrillEmployeeId(emp._id);
    // Persist immediately so the addition survives logout/navigation
    try {
      const empList = allEmployees.filter(e => newIds.includes(e._id));
      const newMergedGrid = { ...mergedGrid, [emp._id]: empShifts };
      await saveGridToServer(newMergedGrid, empList);
    } catch (err) { console.error('Error auto-saving after add:', err); }
  }, [scheduledIds, allEmployees, mergedGrid, periodDates, saveGridToServer]);

  const removeFromSchedule = useCallback(async (empId) => {
    const newIds = scheduledIds.filter(id => id !== empId);
    setScheduledIds(newIds);
    setGridPatch(prev => { const p = { ...prev }; delete p[empId]; return p; });
    setDrillEmployeeId(null);
    try {
      const remainingEmployees = allEmployees.filter(e => newIds.includes(e._id));
      const currentGrid = { ...mergedGrid };
      delete currentGrid[empId];
      await saveGridToServer(currentGrid, remainingEmployees);
    } catch (err) { console.error('Error saving after remove:', err); }
  }, [scheduledIds, allEmployees, mergedGrid, saveGridToServer]);

  // ── Payroll submission ─────────────────────────────────────────────────────
  // Saves the grid, then submits each week in the period to payroll in turn
  // (the submit-to-payroll endpoint itself is unchanged and still only knows
  // about one weekStart at a time). On a 409 conflict for any week, the loop
  // stops and shows the existing dialog — "Overwrite" re-runs the whole loop
  // with overwrite=true, which is safe since already-submitted weeks just get
  // their time entries replaced with identical data.
  const handleSubmitToPayroll = async (overwrite = false) => {
    setConflictDialog(false);
    setSubmitPayrollStatus('submitting');
    try {
      await saveGridToServer(mergedGrid, scheduledEmployees);
      let conflicted = false;
      for (const monday of periodWeekStarts) {
        const weekStart = toISODate(monday);
        const res  = await fetch('/api/schedules/submit-to-payroll', { method: 'POST', headers, body: JSON.stringify({ weekStart, overwrite }) });
        const data = await res.json().catch(() => ({}));
        if (res.status === 409 && data.conflict) { conflicted = true; break; }
        if (!res.ok) throw new Error(data.message || 'Submit failed');
      }
      if (conflicted) { setSubmitPayrollStatus(null); setConflictDialog(true); return; }
      setSubmitPayrollStatus('success');
      setScheduleSubmitted(true);
      setSubmissionStatus('pending');
      setRejectionNotes(null);
      setPendingCount(prev => prev + periodWeekStarts.length);
      setTimeout(() => setSubmitPayrollStatus(null), 4000);
    } catch {
      setSubmitPayrollStatus('error');
      setTimeout(() => setSubmitPayrollStatus(null), 4000);
    }
  };

  // Submit a single employee's schedule to payroll from drill view
  const handleSubmitEmployeeToPayroll = async (empId, overwrite = false) => {
    setDrillSubmitStatus('submitting');
    try {
      await saveGridToServer(mergedGrid, allEmployees.filter(e => e._id === empId));
      let conflicted = false;
      for (const monday of periodWeekStarts) {
        const weekStart = toISODate(monday);
        const res  = await fetch('/api/schedules/submit-to-payroll', {
          method: 'POST', headers,
          body: JSON.stringify({ weekStart, overwrite, employeeId: empId }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.status === 409 && data.conflict) { conflicted = true; break; }
        if (!res.ok) throw new Error(data.message || 'Submit failed');
      }
      if (conflicted && !overwrite) {
        setDrillSubmitStatus('conflict');
        return;
      }
      setDrillSubmitStatus('success');
      setTimeout(() => setDrillSubmitStatus(null), 4000);
    } catch {
      setDrillSubmitStatus('error');
      setTimeout(() => setDrillSubmitStatus(null), 4000);
    }
  };

  // Defaults are a single 7-day weekday-keyed pattern (no date-range concept in
  // the data model) — always derived from the first week of whatever period is
  // currently displayed, regardless of how many weeks are visible.
  const firstWeekDates = useMemo(() => {
    const firstWeekISO = periodWeekStarts[0] ? toISODate(periodWeekStarts[0]) : null;
    return periodDates.filter(pd => pd.weekStartISO === firstWeekISO);
  }, [periodDates, periodWeekStarts]);

  const handleSaveScheduleAndFuture = async () => {
    try {
      await saveGridToServer(mergedGrid, scheduledEmployees);
      const firstWeekISO = toISODate(periodWeekStarts[0]);
      const shiftsByWeek = gridToShiftsByWeek(mergedGrid, scheduledEmployees, firstWeekDates);
      const shifts = (shiftsByWeek[firstWeekISO] || []).filter(s => s.startTime && s.endTime);
      const res = await fetch('/api/schedules/set-default', { method: 'POST', headers, body: JSON.stringify({ shifts }) });
      if (res.ok) {
        await fetchSchedule();
        await fetchDefaultEmployees();
        setSaveStatus('default_saved');
        setTimeout(() => setSaveStatus(null), 3000);
      } else {
        setSaveStatus('error');
        setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
    }
  };

  const handleSetDefault = () => {
    showConfirm('Set the current schedule as the default for all future weeks?', async () => {
      try {
        const employeesToSave = drillEmployeeId
          ? allEmployees.filter(e => e._id === drillEmployeeId)
          : scheduledEmployees;
        const firstWeekISO = toISODate(periodWeekStarts[0]);
        const shiftsByWeek = gridToShiftsByWeek(mergedGrid, employeesToSave, firstWeekDates);
        const shifts = (shiftsByWeek[firstWeekISO] || []).filter(s => s.startTime && s.endTime);
        const res = await fetch('/api/schedules/set-default', { method: 'POST', headers, body: JSON.stringify({ shifts }) });
        if (res.ok) {
          setSaveStatus('default_saved');
          setTimeout(() => setSaveStatus(null), 3000);
          await fetchDefaultEmployees();
        } else {
          setSaveStatus('error');
          setTimeout(() => setSaveStatus(null), 3000);
        }
      } catch { setSaveStatus('error'); setTimeout(() => setSaveStatus(null), 3000); }
    });
  };

  // Loading a default replicates its weekly pattern across every week in the
  // currently displayed period (so a biweekly view gets the same shifts both
  // weeks), rather than just the first week.
  const handleEditDefault = (empId) => {
    const empShifts = defaultSchedulesRaw.filter(s => s.employeeId?.toString() === empId);
    if (!empShifts.length) return;
    const baseGrid = scheduleToGrid({ shifts: empShifts }, allEmployees);
    const periodPattern = applyWeekdayPatternAcrossPeriod(baseGrid[empId], periodDates);
    setScheduledIds(prev => [...new Set([...prev, empId])]);
    setGridPatch(prev => ({ ...prev, [empId]: periodPattern }));
    setShowDefaultModal(false);
    setDrillEmployeeId(empId);
  };

  const handleDeleteDefault = (empId) => {
    showConfirm("Remove this employee's default schedule?", async () => {
      setShowDefaultModal(false);
      try {
        const freshHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user?.token}` };
        const res  = await fetch(`/api/schedules/default/${empId}`, { method: 'DELETE', headers: freshHeaders });
        const data = await res.json();
        if (!res.ok) { alert(data.message); return; }
        const remaining = defaultSchedulesRaw.filter(s => s.employeeId?.toString() !== empId);
        setDefaultSchedulesRaw(remaining);
        await fetchDefaultEmployees();
        setSaveStatus('default_saved');
        setTimeout(() => setSaveStatus(null), 3000);
      } catch { console.error('Error deleting default'); }
    });
  };

  // Copies the previous period (or previous week, in week mode) into the
  // currently displayed period, position-by-position — the Nth day of the
  // previous period maps to the Nth day of this one. If the previous period
  // was shorter (e.g. Feb's 28 days vs March's 31), the extra days simply
  // aren't filled rather than erroring.
  const handleCopyFromLastWeek = useCallback(async (empId) => {
    setCopyingLastWeek(true);
    setCopyLastWeekMsg(null);
    try {
      const prevAnchor = new Date(periodRange.start + 'T00:00:00');
      prevAnchor.setDate(prevAnchor.getDate() - 1);

      let prevRange;
      if (isPayPeriodMode) {
        prevRange = getPayPeriodForDate(payPeriodConfig, prevAnchor);
      }
      if (!prevRange) {
        const prevWeekStart = getMonday(prevAnchor);
        const prevWeekEnd = new Date(prevWeekStart);
        prevWeekEnd.setDate(prevWeekEnd.getDate() + 6);
        prevRange = { start: toISODate(prevWeekStart), end: toISODate(prevWeekEnd) };
      }

      const prevDates      = getPeriodDates(prevRange.start, prevRange.end);
      const prevWeekStarts = getPeriodWeekStarts(prevRange.start, prevRange.end);

      const results = await Promise.all(prevWeekStarts.map(async (monday) => {
        const weekStart = toISODate(monday);
        const res = await fetch(`/api/schedules?weekStart=${weekStart}`, { headers });
        return [weekStart, res.ok ? await res.json() : null];
      }));
      const prevByWeek = Object.fromEntries(results);

      const patch = {};
      let foundAny = false;
      prevDates.forEach((pd, idx) => {
        const targetDate = periodDates[idx];
        if (!targetDate) return;
        const schedule = prevByWeek[pd.weekStartISO];
        const s = schedule?.shifts?.find(sh => (sh.employeeId?.toString?.() || sh.employeeId) === empId && sh.day === pd.day);
        if (!s) return;
        foundAny = true;
        patch[targetDate.dateISO] = {
          startTime:  s.startTime  || '',
          endTime:    s.endTime    || '',
          hours:      s.hours      || 0,
          otHours:    s.otHours    || 0,
          off:        s.off        || false,
          isPTO:      s.isPTO      || false,
          ptoHours:   s.ptoHours   || 0,
          mileage:    s.mileage    || 0,
          notes:      s.notes      || '',
          lunchBreak: s.lunchBreak || { enabled: false, durationMinutes: 30 },
          shift2:     s.shift2     || null,
        };
      });

      if (!foundAny) {
        setCopyLastWeekMsg(isPayPeriodMode ? 'No schedule found for the previous period' : 'No schedule found for last week');
        setTimeout(() => setCopyLastWeekMsg(null), 3000);
        return;
      }

      setGridPatch(prev => ({ ...prev, [empId]: { ...(prev[empId] || {}), ...patch } }));
      setCopyLastWeekMsg(isPayPeriodMode ? 'Previous period copied — review and save when ready' : 'Last week copied — review and save when ready');
      setTimeout(() => setCopyLastWeekMsg(null), 4000);
    } catch {
      setCopyLastWeekMsg('Error copying schedule');
      setTimeout(() => setCopyLastWeekMsg(null), 3000);
    } finally {
      setCopyingLastWeek(false);
    }
  }, [periodRange, periodDates, isPayPeriodMode, payPeriodConfig, headers]);

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      const employeesToSave = drillEmployeeId
        ? allEmployees.filter(e => e._id === drillEmployeeId)
        : scheduledEmployees;
      const firstWeekISO = toISODate(periodWeekStarts[0]);
      const shiftsByWeek = gridToShiftsByWeek(mergedGrid, employeesToSave, firstWeekDates);
      const shifts = (shiftsByWeek[firstWeekISO] || []).filter(s => s.startTime && s.endTime);
      const res = await fetch('/api/schedules/templates', { method: 'POST', headers, body: JSON.stringify({ templateName, shifts }) });
      if (res.ok) {
        const saved = await res.json();
        setTemplates(prev => [saved, ...prev]);
        setTemplateName(''); setShowSaveTemplate(false);
        setSaveStatus('template_saved'); setTimeout(() => setSaveStatus(null), 3000);
      }
    } catch (err) { console.error(err); }
  };

  const handleDrillSaveTemplate = async () => {
    if (!drillTemplateName.trim() || !drillEmployeeId) return;
    const emp = allEmployees.find(e => e._id === drillEmployeeId);
    if (!emp) return;
    try {
      const firstWeekISO = toISODate(periodWeekStarts[0]);
      const shiftsByWeek = gridToShiftsByWeek(mergedGrid, [emp], firstWeekDates);
      const shifts = shiftsByWeek[firstWeekISO] || [];
      const res = await fetch('/api/schedules/templates', { method: 'POST', headers, body: JSON.stringify({ templateName: drillTemplateName, shifts }) });
      if (res.ok) {
        const saved = await res.json();
        setTemplates(prev => [saved, ...prev]);
        setDrillTemplateName(''); setShowDrillSaveTemplate(false);
        setDrillSaveStatus('saved'); setTimeout(() => setDrillSaveStatus(null), 3000);
      }
    } catch (err) { console.error(err); }
  };

  const handleDrillSaveSchedule = async () => {
    try {
      await saveGridToServer(mergedGrid, scheduledEmployees);
      await fetchSchedule();
      setDrillSaveStatus('schedule_saved');
      setTimeout(() => setDrillSaveStatus(null), 3000);
    } catch {
      setDrillSaveStatus('error');
      setTimeout(() => setDrillSaveStatus(null), 3000);
    }
  };

  const handleDrillSaveScheduleAndFuture = async () => {
    if (!drillEmployeeId) return;
    try {
      await saveGridToServer(mergedGrid, scheduledEmployees);

      const empOnly       = allEmployees.filter(e => e._id === drillEmployeeId);
      const firstWeekISO  = toISODate(periodWeekStarts[0]);
      const shiftsByWeek  = gridToShiftsByWeek(mergedGrid, empOnly, firstWeekDates);
      const defaultShifts = (shiftsByWeek[firstWeekISO] || []).filter(s => s.startTime && s.endTime);
      const defRes = await fetch('/api/schedules/set-default', { method: 'POST', headers, body: JSON.stringify({ shifts: defaultShifts }) });
      if (!defRes.ok) throw new Error('Failed to set default');

      await fetchSchedule();
      await fetchDefaultEmployees();
      setDrillSaveStatus('schedule_and_future_saved');
      setTimeout(() => setDrillSaveStatus(null), 4000);
    } catch (err) {
      console.error('Error saving schedule + future:', err);
      setDrillSaveStatus('error');
      setTimeout(() => setDrillSaveStatus(null), 3000);
    }
  };

  const handleLoadTemplate = async (templateId) => {
    if (!templateId) return;
    const tmpl = templates.find(t => t._id === templateId);
    if (!tmpl?.shifts?.length) return;

    const newIds = [...new Set(tmpl.shifts.map(s => {
      const empId = s.employeeId?.toString();
      const found = allEmployees.find(e => e._id === empId || `${e.firstName} ${e.lastName}` === s.employeeName);
      return found?._id;
    }).filter(Boolean))];

    // Templates are a single weekly pattern — replicate it across every week
    // in the currently displayed period.
    const tmplWeekdayGrid = scheduleToGrid(tmpl, allEmployees);
    const periodPatchFor = (empId) => applyWeekdayPatternAcrossPeriod(tmplWeekdayGrid[empId], periodDates);

    setScheduledIds(prev => [...new Set([...prev, ...newIds])]);
    setGridPatch(prev => {
      const p = { ...prev };
      newIds.forEach(id => { p[id] = periodPatchFor(id); });
      return p;
    });
    setDrillEmployeeId(null);
    setShowTemplateDrawer(false);
    setLoadedTemplateId(tmpl._id);
    setLoadedTemplateName(tmpl.templateName);
    loadedTemplateIdRef.current   = tmpl._id;
    loadedTemplateNameRef.current = tmpl.templateName;

    try {
      const allIds  = [...new Set([...scheduledIds, ...newIds])];
      const empList = allEmployees.filter(e => allIds.includes(e._id));
      const merged  = { ...mergedGrid };
      newIds.forEach(id => { merged[id] = periodPatchFor(id); });
      await saveGridToServer(merged, empList);
    } catch (err) { console.error('Error saving loaded template:', err); }
  };


  const handleUpdateTemplate = async () => {
    const templateId = loadedTemplateId || loadedTemplateIdRef.current;
    const templateName = loadedTemplateName || loadedTemplateNameRef.current;
    if (!templateId) return;
    // Use drillEmployeeId if in drill view, otherwise save all scheduled employees
    const empsToSave = drillEmployeeId
      ? allEmployees.filter(e => e._id === drillEmployeeId)
      : scheduledEmployees;
    if (!empsToSave.length) return;
    try {
      const firstWeekISO = toISODate(periodWeekStarts[0]);
      const shiftsByWeek = gridToShiftsByWeek(mergedGrid, empsToSave, firstWeekDates);
      const shifts = (shiftsByWeek[firstWeekISO] || []).filter(s => s.startTime || s.off);
      const res = await fetch(`/api/schedules/templates/${templateId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ shifts }),
      });
      if (res.ok) {
        const updated = await res.json();
        setTemplates(prev => prev.map(t => t._id === loadedTemplateId ? updated : t));
        setDrillSaveStatus('template_updated');
        setTimeout(() => setDrillSaveStatus(null), 3000);
      } else {
        setDrillSaveStatus('error');
        setTimeout(() => setDrillSaveStatus(null), 3000);
      }
    } catch (err) {
      console.error('Error updating template:', err);
      setDrillSaveStatus('error');
      setTimeout(() => setDrillSaveStatus(null), 3000);
    }
  };
  // Update a specific template by ID — used by the picker dropdown in the drill panel
  const handleUpdateSpecificTemplate = async (templateId, templateName) => {
    if (!templateId || !drillEmployeeId) return;
    const emp = allEmployees.find(e => e._id === drillEmployeeId);
    if (!emp) return;
    setShowUpdateTemplatePicker(false);
    try {
      const firstWeekISO = toISODate(periodWeekStarts[0]);
      const shiftsByWeek = gridToShiftsByWeek(mergedGrid, [emp], firstWeekDates);
      const shifts = (shiftsByWeek[firstWeekISO] || []).filter(s => s.startTime || s.off);
      const res = await fetch(`/api/schedules/templates/${templateId}`, {
        method: 'PUT', headers,
        body: JSON.stringify({ shifts }),
      });
      if (res.ok) {
        const updated = await res.json();
        setTemplates(prev => prev.map(t => t._id === templateId ? updated : t));
        setLoadedTemplateId(templateId);
        setLoadedTemplateName(templateName);
        loadedTemplateIdRef.current   = templateId;
        loadedTemplateNameRef.current = templateName;
        setDrillSaveStatus('template_updated');
        setTimeout(() => setDrillSaveStatus(null), 3000);
      } else {
        setDrillSaveStatus('error');
        setTimeout(() => setDrillSaveStatus(null), 3000);
      }
    } catch (err) {
      console.error('Error updating template:', err);
      setDrillSaveStatus('error');
      setTimeout(() => setDrillSaveStatus(null), 3000);
    }
  };

  const handleDeleteTemplate = (id) => {
    showConfirm('Delete this template?', async () => {
      setShowTemplateDrawer(false);
      try {
        const freshHeaders = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${user?.token}` };
        const deleteRes  = await fetch(`/api/schedules/templates/${id}`, { method: 'DELETE', headers: freshHeaders });
        const deleteData = await deleteRes.json();
        if (!deleteRes.ok) { alert(deleteData.message); return; }
        const res = await fetch(`/api/schedules/templates?t=${Date.now()}`, { headers: freshHeaders });
        if (res.ok) { const data = await res.json(); setTemplates(Array.isArray(data) ? data : []); }
      } catch { console.error('Error deleting template'); }
    });
  };

  // In payPeriod mode, prev/next jump by the full period length (landing on
  // the day right after/before the current period's boundary, then letting
  // periodRange re-resolve from there). In week mode, keep the original ±7 days.
  const navigatePeriod = (dir) => {
    if (isPayPeriodMode) {
      const anchor = dir > 0
        ? new Date(periodRange.end + 'T00:00:00')
        : new Date(periodRange.start + 'T00:00:00');
      anchor.setDate(anchor.getDate() + (dir > 0 ? 1 : -1));
      setCurrentMonday(anchor);
    } else {
      const d = new Date(currentMonday);
      d.setDate(d.getDate() + dir * 7);
      setCurrentMonday(d);
    }
    setDrillEmployeeId(null);
  };

  const totalHoursForEmployee = useCallback((empId) =>
    periodDates.reduce((sum, pd) => {
      const s = mergedGrid[empId]?.[pd.dateISO];
      if (!s || s.off) return sum;
      return sum + totalDayHours(s);
    }, 0),
  [mergedGrid, periodDates]);

  // Total OT hours for the period for an employee
  const totalOTHoursForEmployee = useCallback((empId) =>
    periodDates.reduce((sum, pd) => {
      const s = mergedGrid[empId]?.[pd.dateISO];
      if (!s || s.off) return sum;
      return sum + (parseFloat(s.otHours) || 0);
    }, 0),
  [mergedGrid, periodDates]);

  const isToday    = (dayIndex) => periodDates[dayIndex]?.date.toDateString() === new Date().toDateString();
  const getDayDate = (dayIndex) => periodDates[dayIndex]?.date || new Date();

  // Pay-period banner — independent of scheduleViewMode, shown whenever the org
  // has a pay period configured at all, same as before this change.
  const activePayPeriod = useMemo(
    () => payPeriodConfig ? getPayPeriodForDate(payPeriodConfig, currentMonday) : null,
    [payPeriodConfig, currentMonday]
  );

  const submitBtnLabel = () => {
    if (submitPayrollStatus === 'submitting') return 'Submitting…';
    if (submissionStatus === 'approved')      return 'Submit All to Payroll';
    if (submissionStatus === 'pending')       return 'Pending Approval';
    if (submissionStatus === 'rejected')      return 'Pending Approval';
    return 'Pending Approval';
  };

  const submitBtnBg = () => {
    if (submissionStatus === 'approved') return 'linear-gradient(135deg, #6366f1, #4f46e5)';
    return '#9ca3af';
  };

  const inputStyle = { padding: '0.4rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem', width: '100%', boxSizing: 'border-box', background: 'white' };

  const filteredTemplates = useMemo(
    () => templates.filter(t => t.templateName?.toLowerCase().includes(templateSearch.toLowerCase())),
    [templates, templateSearch]
  );

  // ── Shift input renderer ───────────────────────────────────────────────────
  const renderShiftInputs = (empId, day, shift, isShift2 = false) => {
    const lunchOn   = shift.lunchBreak?.enabled;
    const lunchMins = lunchOn ? (shift.lunchBreak.durationMinutes || 30) : 0;
    const isPTO     = !isShift2 && shift.isPTO;
    const otHours   = !isShift2 ? (parseFloat(shift.otHours) || 0) : 0;

    const onTimeChange       = (field, value) => isShift2 ? updateShift2Cell(empId, day, field, value) : updateCell(empId, day, field, value);
    const onToggleLunch      = () => isShift2 ? toggleLunch2(empId, day) : toggleLunch(empId, day);
    const onSetLunchDuration = (mins) => isShift2 ? setLunchDuration2(empId, day, mins) : setLunchDuration(empId, day, mins);

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
        {isShift2 && <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#7c3aed', background: '#ede9fe', borderRadius: '4px', padding: '2px 7px', whiteSpace: 'nowrap' }}>Shift 2</span>}

        {/* Time range */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <TimePicker value={shift.startTime} onChange={val => onTimeChange('startTime', val)} />
          <span style={{ color: '#d1d5db', fontWeight: '700' }}>–</span>
          <TimePicker value={shift.endTime} onChange={val => onTimeChange('endTime', val)} />
        </div>

        {/* Hours */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
          <span style={{ fontSize: '0.78rem', color: '#9ca3af', fontWeight: '600' }}>hrs:</span>
          <input
            type="number" step="0.25" min="0" max="24"
            value={shift.hours || calcHours(shift.startTime, shift.endTime, lunchMins) || ''}
            onChange={e => onTimeChange('hours', parseFloat(e.target.value) || 0)}
            style={{ ...inputStyle, width: '64px' }}
            placeholder="0"
          />
        </div>

        {/* OT — only on primary shift */}
        {!isShift2 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span
              title="Overtime"
              style={{
                fontSize: '0.78rem', fontWeight: '700',
                color: otHours > 0 ? '#d97706' : '#9ca3af',
                cursor: 'default',
                textDecoration: 'underline dotted',
              }}
            >
              OT:
            </span>
            <input
              type="number" step="0.25" min="0" max="24"
              value={otHours || ''}
              onChange={e => updateCell(empId, day, 'otHours', parseFloat(e.target.value) || 0)}
              style={{
                ...inputStyle, width: '64px',
                borderColor: otHours > 0 ? '#f59e0b' : '#d1d5db',
                background: otHours > 0 ? '#fffbeb' : 'white',
              }}
              placeholder="0"
            />
            {otHours > 0 && (
              <span style={{ fontSize: '0.7rem', background: '#fef3c7', color: '#d97706', borderRadius: '4px', padding: '1px 6px', fontWeight: '700' }}>
                {otHours.toFixed(1)}h OT
              </span>
            )}
          </div>
        )}

        {/* PTO — only on primary shift */}
        {!isShift2 && (
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input
              type="checkbox" checked={isPTO}
              onChange={() => togglePTO(empId, day)}
              style={{ width: '14px', height: '14px', accentColor: '#10b981', cursor: 'pointer' }}
            />
            <span
              title="Paid Time Off"
              style={{
                fontSize: '0.78rem', fontWeight: '700',
                color: isPTO ? '#059669' : '#9ca3af',
                cursor: 'pointer',
                textDecoration: 'underline dotted',
              }}
            >
              PTO
            </span>
            {isPTO && (
              <span style={{ fontSize: '0.7rem', background: '#dcfce7', color: '#059669', borderRadius: '4px', padding: '1px 6px', fontWeight: '700' }}>
                {calcHours(shift.startTime, shift.endTime, 0).toFixed(1)}h
              </span>
            )}
          </label>
        )}

        {/* Lunch */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
            <input
              type="checkbox" checked={lunchOn}
              onChange={onToggleLunch}
              style={{ width: '14px', height: '14px', accentColor: '#22c55e', cursor: 'pointer' }}
            />
            <span style={{ fontSize: '0.78rem', fontWeight: '700', color: lunchOn ? '#4f46e5' : '#9ca3af' }}>Lunch</span>
          </label>
          {lunchOn && orgLunchSettings.mode === 'minutes' && (
            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
              {[30, 45, 60].map(mins => (
                <button
                  key={mins}
                  onClick={() => onSetLunchDuration(mins)}
                  style={{
                    padding: '2px 7px', fontSize: '0.72rem', fontWeight: '600', borderRadius: '4px',
                    border: 'none', cursor: 'pointer',
                    background: shift.lunchBreak?.durationMinutes === mins ? '#22c55e' : '#e0e7ff',
                    color: shift.lunchBreak?.durationMinutes === mins ? 'white' : '#4338ca',
                  }}
                >
                  {mins}m
                </button>
              ))}
              <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>-{lunchMins}m</span>
            </div>
          )}
          {lunchOn && orgLunchSettings.mode === 'dollars' && (
            <span style={{ fontSize: '0.72rem', color: '#7c3aed', fontWeight: '600', background: '#ede9fe', borderRadius: '4px', padding: '2px 7px' }}>
              -${(orgLunchSettings.flatRatePerEntry || 0).toFixed(2)} flat
            </span>
          )}
        </div>

        {isShift2 && (
          <button
            onClick={() => removeShift2(empId, day)}
            style={{ marginLeft: 'auto', padding: '0.25rem 0.6rem', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: '600' }}
          >
            Remove
          </button>
        )}
      </div>
    );
  };

  // ── Overview cell ──────────────────────────────────────────────────────────
  const OverviewCell = React.memo(({ shift }) => {
    if (!shift || shift.off) return <div style={{ padding: '0.4rem', textAlign: 'center', color: '#d1d5db', fontSize: '0.7rem', fontWeight: '600' }}>Off</div>;
    const lunchMins = shift.lunchBreak?.enabled ? (shift.lunchBreak.durationMinutes || 30) : 0;
    const hrs   = totalDayHours(shift);
    const otHrs = parseFloat(shift.otHours) || 0;
    return (
      <div style={{ padding: '0.35rem 0.5rem', fontSize: '0.72rem', lineHeight: '1.5' }}>
        {shift.isPTO ? (
          <div style={{ color: '#059669', fontWeight: '700', textAlign: 'center' }}>PTO<br />{hrs.toFixed(1)}h</div>
        ) : (
          <>
            {shift.startTime && <div style={{ color: '#374151', fontWeight: '600' }}>{fmtTime(shift.startTime)}-{fmtTime(shift.endTime)}</div>}
            {shift.shift2?.startTime && <div style={{ color: '#7c3aed', fontWeight: '600', fontSize: '0.68rem' }}>+{fmtTime(shift.shift2.startTime)}-{fmtTime(shift.shift2.endTime)}</div>}
            <div style={{ color: '#22c55e', fontWeight: '700' }}>{hrs.toFixed(1)}h</div>
            {otHrs > 0 && <div style={{ color: '#d97706', fontWeight: '700', fontSize: '0.68rem' }}>{otHrs.toFixed(1)}h OT</div>}
            {lunchMins > 0 && <div style={{ color: '#9ca3af', fontSize: '0.65rem' }}>Lunch {lunchMins}m</div>}
            {(shift.mileage || 0) > 0 && <div style={{ color: '#9ca3af', fontSize: '0.65rem' }}>{shift.mileage}mi</div>}
          </>
        )}
      </div>
    );
  });

  const fmtTime = (t) => {
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${m.toString().padStart(2, '0')}${ampm}`;
  };

  // ── Controls bar ───────────────────────────────────────────────────────────
  const renderControlsBar = () => (
    <div style={{ background: 'white', padding: '1.25rem 1.5rem', borderRadius: '12px', marginBottom: showSaveTemplate ? '0' : '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderBottomLeftRadius: showSaveTemplate ? '0' : '12px', borderBottomRightRadius: showSaveTemplate ? '0' : '12px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          {drillEmployeeId && <button onClick={() => setDrillEmployeeId(null)} style={{ padding: '0.4rem 0.75rem', background: '#f3f4f6', color: '#374151', border: '1.5px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' }}>Back</button>}
          <button onClick={() => navigatePeriod(-1)} style={{ padding: '0.5rem 0.875rem', background: '#1e2a4a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem' }}>prev</button>
          <div style={{ fontWeight: '700', fontSize: '1rem', color: '#111827' }}>{formatPeriodLabel(periodDates)}</div>
          <button onClick={() => navigatePeriod(1)} style={{ padding: '0.5rem 0.875rem', background: '#1e2a4a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '700', fontSize: '1rem' }}>next</button>
          <button onClick={() => { setCurrentMonday(new Date()); setDrillEmployeeId(null); }} style={{ padding: '0.4rem 0.75rem', background: '#e0e7ff', color: '#4338ca', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.8rem' }}>Today</button>
        </div>
        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {canManageSchedules && (<button onClick={() => setShowSaveTemplate(!showSaveTemplate)} style={{ padding: '0.45rem 0.875rem', background: '#1e2a4a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.825rem' }}>Save as Template</button>)}
          {canManageSchedules && (<button onClick={() => setShowTemplateDrawer(true)} style={{ padding: '0.45rem 0.875rem', background: '#f3f4f6', color: '#374151', border: '1.5px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.825rem' }}>My Templates {templates.length > 0 && <span style={{ background: '#22c55e', color: 'white', borderRadius: '99px', padding: '0.05rem 0.4rem', fontSize: '0.7rem', marginLeft: '0.3rem' }}>{templates.length}</span>}</button>)}
          {canManageSchedules && (<button onClick={handleSaveScheduleAndFuture} disabled={scheduledEmployees.length === 0} style={{ padding: '0.45rem 1rem', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.825rem', opacity: scheduledEmployees.length === 0 ? 0.5 : 1 }}>Update This Week + Future</button>)}
          {canManageSchedules && (
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <button onClick={() => { fetchDefaultEmployees(); setShowDefaultModal(true); }} style={{ padding: '0.45rem 1rem', background: '#f0fdf4', color: '#15803d', border: '1.5px solid #86efac', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.825rem' }}>View Defaults</button>
              {defaultEmployees.length > 0 && <span style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#15803d', color: 'white', border: '2px solid white', borderRadius: '99px', minWidth: '20px', height: '20px', fontSize: '0.65rem', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', pointerEvents: 'none' }}>{defaultEmployees.length}</span>}
            </div>
          )}
          <button
            onClick={() => { if (submissionStatus === 'approved') handleSubmitToPayroll(false); }}
            disabled={submitPayrollStatus === 'submitting' || scheduledEmployees.length === 0 || submissionStatus !== 'approved'}
            style={{ padding: '0.45rem 1rem', background: submitBtnBg(), color: 'white', border: 'none', borderRadius: '6px', cursor: submissionStatus === 'approved' ? 'pointer' : 'default', fontWeight: '600', fontSize: '0.825rem', opacity: submitPayrollStatus === 'submitting' ? 0.7 : 1 }}
            title={submissionStatus !== 'approved' ? 'Individual submissions must be approved before full payroll can be submitted' : ''}
          >{submitBtnLabel()}</button>
          {isSuperAdmin && (
            <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
              <button onClick={() => setShowApprovalsPanel(true)} style={{ padding: '0.45rem 1rem', background: '#fef9c3', color: '#92400e', border: '1.5px solid #fde68a', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.825rem' }}>Review Submissions</button>
              {pendingCount > 0 && <span style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#ef4444', color: 'white', border: '2px solid white', borderRadius: '99px', minWidth: '20px', height: '20px', fontSize: '0.65rem', fontWeight: '800', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px', pointerEvents: 'none' }}>{pendingCount}</span>}
            </div>
          )}
        </div>
      </div>
      {activePayPeriod && (
        <div style={{ marginTop: '0.75rem', padding: '0.5rem 0.875rem', background: '#f0f4ff', border: '1px solid #c7d2fe', borderRadius: '8px', display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem' }}>
          <span style={{ fontWeight: '700', color: '#4338ca' }}>Pay Period:</span>
          <span style={{ color: '#374151' }}>
            {formatPayDate(activePayPeriod.start)} – {formatPayDate(activePayPeriod.end)}
          </span>
          {activePayPeriod.payDate && (
            <span style={{ color: '#9ca3af' }}>· Pay date {formatPayDate(activePayPeriod.payDate)}</span>
          )}
        </div>
      )}
      {submissionStatus && <SubmissionBadge status={submissionStatus} reviewNotes={rejectionNotes} reviewedBy={rejectionReviewedBy} reviewedAt={rejectionReviewedAt} />}
      {saveStatus === 'template_saved' && <p style={{ color: '#10b981', fontWeight: '600', marginTop: '0.75rem', marginBottom: 0 }}>Template saved!</p>}
      {saveStatus === 'default_saved'  && <p style={{ color: '#15803d', fontWeight: '600', marginTop: '0.75rem', marginBottom: 0 }}>Default schedule updated!</p>}
      {saveStatus === 'error'          && <p style={{ color: '#ef4444', fontWeight: '600', marginTop: '0.75rem', marginBottom: 0 }}>Error saving. Please try again.</p>}
      {submitPayrollStatus === 'success' && <p style={{ color: '#10b981', fontWeight: '600', marginTop: '0.75rem', marginBottom: 0 }}>Submitted! Awaiting super admin approval.</p>}
      {submitPayrollStatus === 'error'   && <p style={{ color: '#ef4444', fontWeight: '600', marginTop: '0.75rem', marginBottom: 0 }}>Error submitting to payroll. Please try again.</p>}
    </div>
  );

  // ── Overview view ──────────────────────────────────────────────────────────
  const renderOverviewView = () => (
    <div>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#111827' }}>Team Schedule - {formatPeriodLabel(periodDates)}</h2>
          <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#9ca3af' }}>
            {displayedScheduledEmployees.length} scheduled
            {scheduleFilter === 'myTeam' && <span style={{ color: '#4338ca', fontWeight: '700' }}> (My Team)</span>}
            {' '}— Click an employee to edit their shifts
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* My Team filter — only shown for superadmin/adminassistant who manage a team */}
          {myTeamIds.size > 0 && (
            <div style={{ display: 'flex', background: '#f3f4f6', borderRadius: '8px', padding: '3px', gap: '2px' }}>
              <button
                onClick={() => setScheduleFilter('all')}
                style={{ padding: '0.35rem 0.75rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.775rem', background: scheduleFilter === 'all' ? 'white' : 'transparent', color: scheduleFilter === 'all' ? '#111827' : '#6b7280', boxShadow: scheduleFilter === 'all' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.15s' }}
              >
                All Staff
              </button>
              <button
                onClick={() => setScheduleFilter('myTeam')}
                style={{ padding: '0.35rem 0.75rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.775rem', background: scheduleFilter === 'myTeam' ? '#4338ca' : 'transparent', color: scheduleFilter === 'myTeam' ? 'white' : '#6b7280', boxShadow: scheduleFilter === 'myTeam' ? '0 1px 3px rgba(0,0,0,0.15)' : 'none', transition: 'all 0.15s' }}
              >
                My Team ({myTeamIds.size})
              </button>
            </div>
          )}
          {canManageSchedules && (<button onClick={() => { setAddModalSearch(''); setShowAddModal(true); }} style={{ padding: '0.45rem 1rem', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.825rem' }}>+ Add Employee</button>)}
        </div>
      </div>
      {loading ? (
        <div style={{ background: 'white', borderRadius: '12px', padding: '4rem', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}><p style={{ color: '#6b7280' }}>Loading schedule...</p></div>
      ) : scheduledEmployees.length === 0 ? (
        <div style={{ background: 'white', borderRadius: '12px', padding: '4rem 2rem', textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', color: '#9ca3af' }}>
          <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'center' }}><IcoCalendar size={40} color="#22c55e"/></div>
          <p style={{ fontSize: '1.1rem', fontWeight: '600', color: '#6b7280', marginBottom: '0.5rem' }}>No employees scheduled yet</p>
          <p style={{ fontSize: '0.875rem' }}>Load a template or add employees above to get started</p>
        </div>
      ) : (
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '900px' }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                <th style={{ padding: '0.875rem 1rem', textAlign: 'left', fontSize: '0.8rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: '160px', position: 'sticky', left: 0, background: '#f9fafb', zIndex: 1 }}>Employee</th>
                {periodDates.map((pd, i) => {
                  const d = getDayDate(i); const today = isToday(i);
                  const colWidth = periodDates.length > 14 ? '64px' : '110px';
                  return (
                    <th key={pd.dateISO} style={{ padding: '0.875rem 0.5rem', textAlign: 'center', fontSize: '0.8rem', fontWeight: '700', color: today ? '#22c55e' : '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: colWidth, background: today ? '#f0f1ff' : '#f9fafb' }}>
                      <div>{pd.day.slice(0, 3)}</div>
                      <div style={{ fontWeight: '500', textTransform: 'none', fontSize: '0.72rem' }}>{d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                    </th>
                  );
                })}
                <th style={{ padding: '0.875rem 0.75rem', textAlign: 'center', fontSize: '0.8rem', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', background: '#f9fafb' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {displayedScheduledEmployees.map((emp, rowIdx) => {
                const totalHrs   = totalHoursForEmployee(emp._id);
                const totalOTHrs = totalOTHoursForEmployee(emp._id);
                const isMyTeam   = myTeamIds.has(emp._id);
                return (
                  <tr key={emp._id} onClick={() => setDrillEmployeeId(emp._id)} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', background: isMyTeam && scheduleFilter === 'all' ? '#f5f3ff' : rowIdx % 2 === 0 ? 'white' : '#fafafa', transition: 'background 0.1s', borderLeft: isMyTeam ? '3px solid #7c3aed' : '3px solid transparent' }} onMouseEnter={e => e.currentTarget.style.background = '#f0f1ff'} onMouseLeave={e => e.currentTarget.style.background = isMyTeam && scheduleFilter === 'all' ? '#f5f3ff' : rowIdx % 2 === 0 ? 'white' : '#fafafa'}>
                    <td style={{ padding: '0.75rem 1rem', position: 'sticky', left: 0, background: 'inherit', zIndex: 1, borderRight: '1px solid #f3f4f6' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                        <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: '700', flexShrink: 0 }}>{emp.firstName?.[0]}{emp.lastName?.[0]}</div>
                        <div>
                          <div style={{ fontSize: '0.85rem', fontWeight: '700', color: '#111827' }}>{emp.firstName} {emp.lastName}</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                            <span style={{ fontSize: '0.7rem', color: '#9ca3af', textTransform: 'capitalize' }}>{emp.role}</span>
                            {isMyTeam && <span style={{ fontSize: '0.62rem', fontWeight: '700', background: '#ede9fe', color: '#7c3aed', borderRadius: '4px', padding: '1px 5px' }}>My Team</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    {periodDates.map((pd, i) => (
                      <td key={pd.dateISO} style={{ padding: '0', verticalAlign: 'top', background: isToday(i) ? '#f8f9ff' : 'inherit', borderLeft: '1px solid #f3f4f6' }}>
                        <OverviewCell shift={mergedGrid[emp._id]?.[pd.dateISO]} />
                      </td>
                    ))}
                    <td style={{ padding: '0.75rem', textAlign: 'center', borderLeft: '2px solid #e5e7eb' }}>
                      <div style={{ fontSize: '0.95rem', fontWeight: '800', color: '#22c55e' }}>{totalHrs.toFixed(1)}h</div>
                      {totalOTHrs > 0 && (
                        <div title="Overtime" style={{ fontSize: '0.75rem', fontWeight: '700', color: '#d97706', marginTop: '2px' }}>
                          +{totalOTHrs.toFixed(1)}h OT
                        </div>
                      )}
                      {canManageSchedules && (<button onClick={e => { e.stopPropagation(); e.preventDefault(); removeFromSchedule(emp._id); }} style={{ marginTop: '0.25rem', fontSize: '0.65rem', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px' }}>Remove</button>)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  // ── Drill view ─────────────────────────────────────────────────────────────
  const renderDrillView = () => {
    if (!drillEmployee) return null;
    const empId = drillEmployee._id;
    const hasShift2 = (day) => !!mergedGrid[empId]?.[day]?.shift2;
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: copyLastWeekMsg ? '0.5rem' : '1.25rem', flexWrap: 'wrap' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.85rem', fontWeight: '700', flexShrink: 0 }}>{drillEmployee.firstName?.[0]}{drillEmployee.lastName?.[0]}</div>
          <div style={{ flex: 1 }}>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>{drillEmployee.firstName} {drillEmployee.lastName}</h2>
            <p style={{ margin: 0, fontSize: '0.8rem', color: '#9ca3af', textTransform: 'capitalize' }}>
              {drillEmployee.role} — {totalHoursForEmployee(empId).toFixed(1)}h
              {totalOTHoursForEmployee(empId) > 0 && (
                <span title="Overtime" style={{ marginLeft: '6px', color: '#d97706', fontWeight: '700' }}>
                  +{totalOTHoursForEmployee(empId).toFixed(1)}h OT
                </span>
              )}
              {' '}this {isPayPeriodMode ? 'period' : 'week'}
            </p>
          </div>
          {canManageSchedules && (
            <button
              onClick={() => handleCopyFromLastWeek(empId)}
              disabled={copyingLastWeek}
              title={isPayPeriodMode ? "Load this employee's shifts from the previous period as a starting point" : "Load this employee's shifts from the previous week as a starting point"}
              style={{ padding: '0.4rem 0.875rem', background: '#f8faff', color: '#4338ca', border: '1.5px solid #c7d2fe', borderRadius: '6px', cursor: copyingLastWeek ? 'not-allowed' : 'pointer', fontWeight: '600', fontSize: '0.8rem', opacity: copyingLastWeek ? 0.6 : 1, whiteSpace: 'nowrap' }}
            >
              {copyingLastWeek ? '⏳ Copying...' : isPayPeriodMode ? '⏪ Copy from Previous Period' : '⏪ Copy from Last Week'}
            </button>
          )}
        </div>
        {copyLastWeekMsg && (
          <p style={{ marginBottom: '1rem', fontSize: '0.8rem', fontWeight: '600', color: copyLastWeekMsg.startsWith('No') || copyLastWeekMsg.startsWith('Error') || copyLastWeekMsg.startsWith('Could') ? '#92400e' : '#1e40af', background: copyLastWeekMsg.startsWith('No') || copyLastWeekMsg.startsWith('Error') || copyLastWeekMsg.startsWith('Could') ? '#fef3c7' : '#eff6ff', padding: '0.5rem 0.875rem', borderRadius: '6px' }}>
            {copyLastWeekMsg}
          </p>
        )}
        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
          {periodDates.map((pd, i) => {
            const day      = pd.dateISO;
            const shift    = mergedGrid[empId]?.[day] || emptyShift();
            const isPTO    = shift.isPTO;
            const dayDate  = getDayDate(i);
            const todayRow = isToday(i);
            const shift2   = hasShift2(day);
            const dayTotal = totalDayHours(shift);
            const dayOT    = parseFloat(shift.otHours) || 0;
            return (
              <div key={day} style={{ display: 'grid', gridTemplateColumns: '160px 1fr', borderBottom: '1px solid #f3f4f6', background: todayRow ? '#f8f9ff' : shift.off ? '#fef9f9' : isPTO ? '#f0fdf4' : 'white' }}>
                <div style={{ padding: '1rem 1.25rem', borderRight: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <div style={{ fontWeight: '700', fontSize: '0.95rem', color: todayRow ? '#22c55e' : '#111827' }}>{pd.day}</div>
                  <div style={{ fontSize: '0.78rem', color: todayRow ? '#22c55e' : '#9ca3af', marginTop: '1px' }}>
                    {dayDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    {todayRow && <span style={{ marginLeft: '6px', fontSize: '0.65rem', background: '#22c55e', color: 'white', borderRadius: '4px', padding: '1px 5px', fontWeight: '700' }}>TODAY</span>}
                  </div>
                  {!shift.off && (
                    <div style={{ marginTop: '4px' }}>
                      {shift2 && <div style={{ fontSize: '0.68rem', color: '#7c3aed', fontWeight: '700' }}>{dayTotal.toFixed(1)}h total</div>}
                      {dayOT > 0 && <div title="Overtime" style={{ fontSize: '0.68rem', color: '#d97706', fontWeight: '700' }}>{dayOT.toFixed(1)}h OT</div>}
                    </div>
                  )}
                </div>
                <div style={{ padding: '0.75rem 1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {shift.off ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                      <span style={{ color: '#ef4444', fontSize: '0.85rem', fontWeight: '600' }}>Day Off</span>
                      {canManageSchedules && <button onClick={() => toggleOff(empId, day)} style={{ padding: '0.25rem 0.6rem', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: '600' }}>Undo</button>}
                    </div>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
                        {shift2 && <span style={{ fontSize: '0.72rem', fontWeight: '700', color: '#22c55e', background: '#e0e7ff', borderRadius: '4px', padding: '2px 7px' }}>Shift 1</span>}
                        {renderShiftInputs(empId, day, shift, false)}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <span style={{ fontSize: '0.78rem', color: '#9ca3af', fontWeight: '600' }}>Miles</span>
                          <input type="number" step="0.1" min="0" placeholder="0" value={shift.mileage || ''} onChange={e => updateCell(empId, day, 'mileage', parseFloat(e.target.value) || 0)} style={{ ...inputStyle, width: '72px' }} />
                        </div>
                        {canManageSchedules && (<button onClick={() => toggleOff(empId, day)} style={{ padding: '0.3rem 0.75rem', background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}>Mark Off</button>)}{canManageSchedules && (<button onClick={() => clearDay(empId, day)} style={{ padding: '0.3rem 0.75rem', background: '#fff1f2', color: '#e11d48', border: '1px solid #fecdd3', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}><IcoTrash size={13}/></button>)}
                      </div>
                      {shift2 && <div style={{ borderTop: '1px dashed #e5e7eb', paddingTop: '0.5rem' }}>{renderShiftInputs(empId, day, mergedGrid[empId]?.[day]?.shift2, true)}</div>}
                      {!shift2 && canManageSchedules && (<button onClick={() => addShift2(empId, day)} style={{ alignSelf: 'flex-start', padding: '0.25rem 0.75rem', background: 'none', color: '#7c3aed', border: '1.5px dashed #c4b5fd', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '600' }}>+ Add Shift</button>)}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-dashboard">
      <div className="dashboard-content">
        <section className="welcome-section">
          <h1>Schedule</h1>
          <p className="subtitle">{drillEmployeeId ? `Editing ${drillEmployee?.firstName}'s schedule` : 'Team weekly overview'}</p>
        </section>

        {renderControlsBar()}

        {showSaveTemplate && canManageSchedules && (
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', alignItems: 'center', background: 'white', padding: '1rem 1.5rem', borderRadius: '0 0 12px 12px', boxShadow: '0 2px 4px rgba(0,0,0,0.08)' }}>
            <input type="text" placeholder="Template name" value={templateName} onChange={e => setTemplateName(e.target.value)} style={{ flex: 1, padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }} />
            <button onClick={handleSaveTemplate} style={{ padding: '0.5rem 1rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Save</button>
            <button onClick={() => setShowSaveTemplate(false)} style={{ padding: '0.5rem 1rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
          </div>
        )}

        {drillEmployeeId ? renderDrillView() : renderOverviewView()}

        {drillEmployeeId && drillEmployee && canManageSchedules && (
          <div style={{ marginTop: '1.25rem', background: 'white', borderRadius: '12px', padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <button onClick={handleDrillSaveSchedule} style={{ padding: '0.6rem 1.25rem', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>Update This Week</button>
              <button onClick={handleDrillSaveScheduleAndFuture} style={{ padding: '0.6rem 1.25rem', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>Update This Week + Future</button>
              <button
                onClick={() => drillSubmitStatus === 'conflict' ? handleSubmitEmployeeToPayroll(drillEmployeeId, true) : handleSubmitEmployeeToPayroll(drillEmployeeId)}
                disabled={drillSubmitStatus === 'submitting'}
                style={{ padding: '0.6rem 1.25rem', background: drillSubmitStatus === 'conflict' ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'linear-gradient(135deg, #6366f1, #4f46e5)', color: 'white', border: 'none', borderRadius: '8px', cursor: drillSubmitStatus === 'submitting' ? 'default' : 'pointer', fontWeight: '600', fontSize: '0.875rem', opacity: drillSubmitStatus === 'submitting' ? 0.7 : 1 }}>
                {drillSubmitStatus === 'submitting' ? 'Submitting…' : drillSubmitStatus === 'conflict' ? 'Entries exist — Overwrite?' : 'Submit to Payroll'}
              </button>

              {/* Update Template — shows loaded template name if one was loaded, otherwise shows a picker */}
              {(loadedTemplateId || loadedTemplateIdRef.current) ? (
                <button onClick={handleUpdateTemplate} style={{ padding: '0.6rem 1.25rem', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  <IcoRefreshCw size={14}/> Update "{loadedTemplateName || loadedTemplateNameRef.current}"
                </button>
              ) : (
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setShowUpdateTemplatePicker(!showUpdateTemplatePicker)}
                    style={{ padding: '0.6rem 1.25rem', background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}
                  >
                    <IcoRefreshCw size={14}/> Update Template ▾
                  </button>
                  {showUpdateTemplatePicker && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 100, minWidth: '240px', maxHeight: '280px', overflowY: 'auto' }}>
                      <div style={{ padding: '0.6rem 0.875rem', borderBottom: '1px solid #f3f4f6', fontSize: '0.75rem', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Pick a template to update
                      </div>
                      {templates.length === 0 ? (
                        <div style={{ padding: '1rem', fontSize: '0.875rem', color: '#9ca3af', textAlign: 'center' }}>No templates saved yet</div>
                      ) : (
                        templates.map(tmpl => (
                          <div
                            key={tmpl._id}
                            onClick={() => handleUpdateSpecificTemplate(tmpl._id, tmpl.templateName)}
                            style={{ padding: '0.65rem 0.875rem', cursor: 'pointer', fontSize: '0.875rem', color: '#111827', borderBottom: '1px solid #f9fafb', transition: 'background 0.1s' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#fef3c7'}
                            onMouseLeave={e => e.currentTarget.style.background = 'white'}
                          >
                            <div style={{ fontWeight: '600' }}>{tmpl.templateName}</div>
                            <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '2px' }}>
                              {[...new Set(tmpl.shifts?.map(s => s.employeeName) || [])].slice(0, 2).join(', ')}
                              {(tmpl.shifts?.length || 0) > 2 ? ' +more' : ''}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                </div>
              )}

              <button onClick={() => setShowDrillSaveTemplate(!showDrillSaveTemplate)} style={{ padding: '0.6rem 1.25rem', background: '#1e2a4a', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem' }}>Save as Template</button>
              <span style={{ fontSize: '0.75rem', color: '#9ca3af', fontStyle: 'italic' }}>"Update This Week + Future" also sets this as {drillEmployee.firstName}'s default going forward</span>
            </div>
            {showDrillSaveTemplate && (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <input type="text" placeholder={`e.g. ${drillEmployee.firstName}'s Schedule`} value={drillTemplateName} onChange={e => setDrillTemplateName(e.target.value)} style={{ flex: 1, padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' }} />
                <button onClick={handleDrillSaveTemplate} style={{ padding: '0.5rem 1rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Save</button>
                <button onClick={() => { setShowDrillSaveTemplate(false); setDrillTemplateName(''); }} style={{ padding: '0.5rem 1rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
              </div>
            )}
            {drillSaveStatus === 'template_updated' && <p style={{ margin: 0, color: '#d97706', fontWeight: '600', fontSize: '0.875rem' }}>Template updated!</p>}
            {drillSaveStatus === 'saved'                     && <p style={{ margin: 0, color: '#10b981', fontWeight: '600', fontSize: '0.875rem' }}>Template saved!</p>}
            {drillSaveStatus === 'schedule_saved'            && <p style={{ margin: 0, color: '#22c55e', fontWeight: '600', fontSize: '0.875rem' }}>This week's schedule updated!</p>}
            {drillSaveStatus === 'schedule_and_future_saved' && <p style={{ margin: 0, color: '#10b981', fontWeight: '600', fontSize: '0.875rem' }}>Schedule updated for this week and set as default for future weeks!</p>}
            {drillSaveStatus === 'error'                     && <p style={{ margin: 0, color: '#ef4444', fontWeight: '600', fontSize: '0.875rem' }}>Error saving. Try again.</p>}
            {drillSubmitStatus === 'success'  && <p style={{ margin: 0, color: '#6366f1', fontWeight: '600', fontSize: '0.875rem' }}>Submitted to payroll! Awaiting approval.</p>}
            {drillSubmitStatus === 'error'    && <p style={{ margin: 0, color: '#ef4444', fontWeight: '600', fontSize: '0.875rem' }}>Error submitting to payroll. Try again.</p>}
          </div>
        )}

        {/* Dialogs */}
        {conflictDialog && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'white', borderRadius: '14px', padding: '2rem', maxWidth: '420px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ marginBottom: '0.75rem', textAlign: 'center', display: 'flex', justifyContent: 'center' }}><IcoAlertTriangle size={32} color="#d97706"/></div>
              <h3 style={{ fontWeight: '700', fontSize: '1.1rem', marginBottom: '0.75rem', textAlign: 'center', color: '#111827' }}>Time Entries Already Exist</h3>
              <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1.5rem', textAlign: 'center', lineHeight: '1.6' }}>Some employees already have time entries for this week.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <button onClick={() => handleSubmitToPayroll(true)} style={{ padding: '0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem' }}>Overwrite Existing Entries</button>
                <button onClick={() => setConflictDialog(false)} style={{ padding: '0.75rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem' }}>Cancel</button>
              </div>
            </div>
          </div>
        )}
        {confirmDialog && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: 'white', borderRadius: '14px', padding: '2rem', maxWidth: '380px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <p style={{ margin: '0 0 1.5rem', fontSize: '1rem', color: '#111827', textAlign: 'center', lineHeight: '1.6' }}>{confirmDialog.message}</p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={() => setConfirmDialog(null)} style={{ flex: 1, padding: '0.75rem', background: '#4b5563', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                <button onClick={() => { confirmDialog.onConfirm(); setConfirmDialog(null); }} style={{ flex: 1, padding: '0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>Confirm</button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showDefaultModal && (
        <div onClick={() => setShowDefaultModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', padding: '1.5rem', width: '440px', maxWidth: '90vw', maxHeight: '75vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>Default Schedules</h2>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.775rem', color: '#9ca3af' }}>{defaultEmployees.length} employee{defaultEmployees.length !== 1 ? 's' : ''} with a saved default</p>
              </div>
              <button onClick={() => setShowDefaultModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: '#9ca3af' }}>X</button>
            </div>
            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {defaultLoading ? <p style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>Loading...</p>
                : defaultEmployees.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}><p style={{ fontWeight: '600', color: '#6b7280', margin: 0 }}>No default schedules set yet</p></div>
                ) : defaultEmployees.map(emp => (
                  <div key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', borderRadius: '8px', background: '#f0fdf4', border: '1px solid #86efac' }}>
                    <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: '700', flexShrink: 0 }}>{emp.name.split(' ').map(n => n[0]).join('').slice(0, 2)}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>{emp.name}</div>
                      {emp.role && <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'capitalize' }}>{emp.role}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: '0.4rem' }}>
                      <button onClick={() => handleEditDefault(emp.id)} style={{ padding: '0.3rem 0.65rem', background: '#e0e7ff', color: '#4338ca', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '700' }}>Edit</button>
                      <button onClick={() => handleDeleteDefault(emp.id)} style={{ padding: '0.3rem 0.65rem', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: '700' }}>Delete</button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      )}

      {showAddModal && canManageSchedules && (
        <div onClick={() => setShowAddModal(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '14px', padding: '1.5rem', width: '420px', maxWidth: '90vw', maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>Add Employees to Schedule</h2>
              <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: '#9ca3af' }}>X</button>
            </div>
            <input type="text" placeholder="Search employees..." value={addModalSearch} onChange={e => setAddModalSearch(e.target.value)} style={{ padding: '0.5rem 0.75rem', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '0.875rem', marginBottom: '1rem', outline: 'none' }} />
            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {allEmployees.filter(e => `${e.firstName} ${e.lastName}`.toLowerCase().includes(addModalSearch.toLowerCase())).map(emp => {
                const isScheduled = scheduledIds.includes(emp._id);
                return (
                  <div key={emp._id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0.75rem', borderRadius: '8px', background: isScheduled ? '#f0fdf4' : '#f9fafb', border: `1px solid ${isScheduled ? '#86efac' : '#e5e7eb'}` }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: isScheduled ? '#10b981' : 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.75rem', fontWeight: '700' }}>{emp.firstName?.[0]}{emp.lastName?.[0]}</div>
                      <div>
                        <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#111827' }}>{emp.firstName} {emp.lastName}</div>
                        <div style={{ fontSize: '0.72rem', color: '#9ca3af', textTransform: 'capitalize' }}>{emp.role}</div>
                      </div>
                    </div>
                    {isScheduled ? <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: '700' }}>Scheduled</span>
                      : <button onClick={() => { addToSchedule(emp); setShowAddModal(false); }} style={{ padding: '0.3rem 0.75rem', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '600' }}>+ Add</button>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {showTemplateDrawer && <div onClick={() => setShowTemplateDrawer(false)} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1500 }} />}
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '380px', background: 'white', boxShadow: '-4px 0 24px rgba(0,0,0,0.12)', zIndex: 1600, transform: showTemplateDrawer ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 0.3s ease', display: 'flex', flexDirection: 'column', overflowY: 'auto' }}>
        <div style={{ padding: '1.5rem', borderBottom: '1px solid #f3f4f6', display: 'flex', flexDirection: 'column', gap: '0.75rem', position: 'sticky', top: 0, background: 'white', zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#111827' }}>Saved Templates</h2>
              <p style={{ margin: '0.2rem 0 0', fontSize: '0.775rem', color: '#9ca3af' }}>{templates.length} template{templates.length !== 1 ? 's' : ''} saved</p>
            </div>
            <button onClick={() => setShowTemplateDrawer(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: '#9ca3af' }}>X</button>
          </div>
          <input type="text" placeholder="Search templates..." value={templateSearch} onChange={e => setTemplateSearch(e.target.value)} style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1.5px solid #e5e7eb', borderRadius: '8px', fontSize: '0.85rem', boxSizing: 'border-box', outline: 'none' }} />
        </div>
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filteredTemplates.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '3rem 1rem', color: '#9ca3af' }}>
              <p style={{ fontWeight: '600', color: '#6b7280', margin: '0 0 0.25rem' }}>{templates.length === 0 ? 'No templates yet' : 'No templates match your search'}</p>
              <p style={{ fontSize: '0.825rem', margin: 0 }}>{templates.length === 0 ? 'Save a schedule as a template to reuse it' : 'Try a different search term'}</p>
            </div>
          ) : filteredTemplates.map(tmpl => {
            const uniqueEmps  = [...new Set(tmpl.shifts?.map(s => s.employeeName) || [])];
            const totalHours  = tmpl.shifts?.reduce((sum, s) => sum + (s.off ? 0 : s.hours || 0), 0) || 0;
            return (
              <div key={tmpl._id} style={{ background: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                <div style={{ padding: '1rem', borderBottom: '1px solid #e5e7eb', background: 'white' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <p style={{ margin: 0, fontWeight: '700', fontSize: '0.95rem', color: '#111827' }}>{tmpl.templateName}</p>
                      <p style={{ margin: '0.2rem 0 0', fontSize: '0.775rem', color: '#9ca3af' }}>{uniqueEmps.length} employee{uniqueEmps.length !== 1 ? 's' : ''} - {totalHours.toFixed(1)}h total</p>
                    </div>
                    <button onClick={() => handleDeleteTemplate(tmpl._id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', fontSize: '0.875rem' }}>Delete</button>
                  </div>
                </div>
                <div style={{ padding: '0.75rem 1rem' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem', marginBottom: '0.75rem' }}>
                    {uniqueEmps.slice(0, 6).map(name => <span key={name} style={{ padding: '0.2rem 0.5rem', background: '#e0e7ff', color: '#4338ca', borderRadius: '99px', fontSize: '0.72rem', fontWeight: '600' }}>{name}</span>)}
                    {uniqueEmps.length > 6 && <span style={{ padding: '0.2rem 0.5rem', background: '#f3f4f6', color: '#6b7280', borderRadius: '99px', fontSize: '0.72rem' }}>+{uniqueEmps.length - 6} more</span>}
                  </div>
                  <button onClick={() => handleLoadTemplate(tmpl._id)} style={{ width: '100%', padding: '0.55rem', background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600', fontSize: '0.825rem' }}>Load This Template</button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {showApprovalsPanel && (
        <PendingApprovalsPanel headers={headers} onClose={() => { setShowApprovalsPanel(false); fetchSubmissionStatus(); setPendingCount(0); }} organizationId={user?.organizationId} />
      )}
    </div>
  );
}